import axios, { AxiosError, AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';

import { isProduction } from './constants';
import { SVError } from './errors';
import { errorMessage } from './helpers';

// https://nodejs.org/api/cli.html#cli_node_tls_reject_unauthorized_value
if (!isProduction) {
  process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';
}

/**
 *
 * @param error Axios response object
 */
export const logError: (error?: AxiosError) => string | undefined = error => {
  if (isProduction || !error) {
    return undefined;
  }
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    errorMessage(error.response.data);
    errorMessage(error.response.status);
    errorMessage(error.response.headers);

    return `data: ${error.response.data}, status: ${error.response.status}, headers: ${error.response.headers}`;
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    errorMessage(error.request);

    return `request: ${error.request}`;
  } else {
    // Something happened in setting up the request that triggered an Error
    errorMessage('Error: ', error.message);

    return `message: ${error.message}`;
  }
};

/**
 * Type-guard function to determine of given object isntanceOf AxiosError
 * @param err
 */
export function isAxiosError(err: any): err is AxiosError {
  return err &&
    err.response &&
    err.response.data &&
    err.response.status &&
    err.response.headers &&
    err.request
    ? true
    : false;
}

/**
 * Wrapper for accessing and managing API requests (Get/Post/Put/Delete)
 */
export class Adapter {
  /**
   * Reference to an instance of the api
   */
  private instance: AxiosInstance;

  constructor(config: AxiosRequestConfig) {
    // Use these instances of axios to make network requests to the testservice API's
    this.instance = axios.create(config);
  }

  updateConfig = (config: AxiosRequestConfig) => {
    this.instance.defaults = config;
  };

  async getValue<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return this.instance.get(url, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }

  async postValue<T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return this.instance.post(url, body, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }

  async putValue<T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return this.instance.put(url, body, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }

  async deleteValue<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return this.instance.delete(url, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new SVError(JSON.stringify(error)));
    }
  }
}
